'use client';
import { isSDXL } from '@/components/shared/generators/isSDXL';
import {
  getGeneratorList,
  getGifModelList,
  GetGeneratorRes,
  MediaResult,
  pollGenerationJob
} from '@/lib/api/generate';
import { useGlobalContext } from '@/app/context/store';
import { getJobHistory, HistoryJob } from '@/lib/api/job';
import { upscaleImage } from '@/lib/api/upscale';
import { GENERATOR_LIST_QUERY_KEY, JOB_HISTORY_QUERY_KEY } from '@/lib/utils/queryKeys';
import { useQuery } from '@tanstack/react-query';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';

export interface Tag {
  id: string;
  name: string;
  selected: boolean;
  savedTag?: boolean;
  masterTag?: string;
}

export interface Generator {
  _id: string;
  frontend: {
    cfgU: number;
    cfgM: number;
    cfgL: number;
    styleImage: string;
  };
  names: Array<{
    code: string;
    value: string;
  }>;
  shortName: string;
  description: string;
  isSDXL: boolean;
  creditCost: {
    aspectRatioCost: number;
    cfgScaleCost: number;
    characterCost: number;
    freeTagLimit: number;
    generatorCost: number;
    premiumCost: number;
    promptCost: number;
    promptLimit: number;
    tagCost: number;
    upscaleCost: number;
  };
  upscaleSettings?: {
    upscalePricing: Array<{
      factor: number;
      cost: number;
    }>;
    upscaleStyle?: string;
  };
}

// TODO: check usages
export interface TagGroup {
  id: string;
  name: string;
  type: string;
  tags: Tag[];
  tagIds?: string[];
}

export interface TransformTagGroup {
  _id: string;
  names: Record<string, string>;
  type: string;
  tags: TransformTag[];
  tagIds?: string[];
}

export interface TransformTag {
  _id: string;
  masterTag: string;
  names: {
    [key: string]: string;
  };
  selected: boolean;
  ugc: boolean;
}

interface CreditCost {
  generatorCost: number;
  premiumCost: number;
  upscaleCost: number;
  freeTagLimit: number;
  tagCost: number;
  characterCost: number;
  cfgScaleCost: number;
  aspectRatioCost: number;
  promptLimit: number;
  promptCost: number;
}

export enum DisplayLoadingState {
  Default = 0,
  Generating = 1, // maybe change this to queueing?
  Loaded = 2, // maybe change this to completed?
  ErrorJobLimit = 3,
  Error400 = 4,
  ErrorOutOfCredits = 5,
  ErrorGeneric = 6,
  Delaying = 7,
  Queueing = 9,
  ErrorSensitiveContent = 11
}

export interface PollJob {
  jobId: string;
  delayedUntil?: Date;
  jobType?: JobType;
}

export interface DisplayMedia {
  id: string;
  generatorId?: string;
  type: 'image' | 'gif';
  imageUrl?: string;
  videoUrl?: string;
  thumbnailUrl?: string;
  saved: boolean;
  isSaving: boolean;
  upscaled?: boolean;
  upscaleFactor?: number;
  originalGenerationId?: string;
  referenceGifId?: string;
  frameRate?: number;
}

export enum ActiveTab {
  Generation,
  History
}

export enum JobStatus {
  Completed = 'completed',
  Active = 'active',
  Delayed = 'delayed',
  Pending = 'pending',
  Cancelled = 'cancelled',
  Failed = 'failed',
  Aborted = 'aborted'
}

export enum QueueType {
  Free = 0,
  Turbo = 1,
  Video = 2
}

export enum JobType {
  Generation = 'generateImage',
  BatchGeneration = 'batchGenerateImage',
  Upscale = 'upscaleImage',
  Video = 'generateVideo',
  GifChaining = 'gifChaining'
}

export interface SourceImage {
  _id: string;
  imageUrl: string;
  isGif?: boolean; // Whether this source is from a GIF (for extend functionality)
  frameRate?: number; // Frame rate of the source GIF
}

export interface GeneratorState {
  selectedGenerator: {
    _id: string;
    frontend: {
      styleImage: any;
      cfgU: number;
      cfgM: number;
      cfgL: number;
    };
    shortName: string;
    names: Array<{
      code: string;
      value: string;
    }>;
  };
  selectedStyleUgc: {
    name: string;
    tagGroups: any[];
  };
  selectedMasterUgc: {
    id: string;
    style: string;
    tag: { names?: any };
  };
  selectedStyleGroupsUgc: string[];
  selectedStyleCreateUgc: {
    name: string;
    _id: string;
    tagGroups: any[];
  };
  selectedStyleGroupCreateUgc: any;
  positivePromptCreateUgc: string;
  negativePromptCreateUgc: string;
  nameCreateUgc: string;
  creditCost: CreditCost;
  currentQueueType: QueueType;
  queueType: QueueType;
  freeQueueDelay: Date;
  customPrompt: string;
  customNegative: string;
  cfgScale: number;
  resolution: number;
  denoisingStrength: number;
  seed: number;
  displayImage: string;
  queuePosition: string;
  saveState: number;
  upscaleState: number;
  detectCelebrity: boolean;
  generationType: 'image' | 'gif';
  customPromptGif: string;
  customPromptGifImage: string; // For gif chaining - image prompt
  customPromptGifMotion: string; // For gif chaining - motion prompt
  gifAspectRatio: string;
  gifLength: number;
  imageToGifLength: number;
  imageToGifFramerate: 30 | 60;
  sourceImage: SourceImage | null;
}

const defaultState: GeneratorState = {
  selectedGenerator: {
    _id: '',
    names: [],
    frontend: {
      styleImage: '',
      cfgU: 0.0,
      cfgM: 0.0,
      cfgL: 0.0
    },
    shortName: ''
  },
  selectedStyleUgc: { name: '', tagGroups: [] },
  selectedStyleGroupsUgc: ['all'],
  selectedMasterUgc: { id: '', style: '', tag: {} },
  selectedStyleCreateUgc: { name: '', _id: '', tagGroups: [] },
  selectedStyleGroupCreateUgc: { _id: '', names: [] },
  positivePromptCreateUgc: '',
  negativePromptCreateUgc: '',
  nameCreateUgc: '',
  creditCost: {
    generatorCost: 0,
    premiumCost: 0,
    upscaleCost: 0,
    freeTagLimit: 0,
    tagCost: 0,
    characterCost: 0,
    cfgScaleCost: 0,
    aspectRatioCost: 0,
    promptLimit: 0,
    promptCost: 0
  },
  currentQueueType: QueueType.Free,
  queueType: QueueType.Free,
  freeQueueDelay: new Date('January 1, 1970 00:00:00 UTC'),
  customPrompt: '',
  customNegative: '',
  cfgScale: 0.0,
  resolution: 1,
  denoisingStrength: 0,
  seed: 0,
  saveState: 0,
  upscaleState: 0,
  displayImage: '',
  queuePosition: '',
  detectCelebrity: false,
  generationType: 'image',
  customPromptGif: '',
  customPromptGifImage: '', // For gif chaining - image prompt
  customPromptGifMotion: '', // For gif chaining - motion prompt
  gifAspectRatio: '2:3',
  gifLength: 2,
  imageToGifLength: 5,
  imageToGifFramerate: 30,
  sourceImage: null
};

interface GifGenerator {
  _id: string;
  name: string;
  shortName: string;
  creditCost: CreditCost;
  frontend?: {
    styleImage: string;
  };
  workflowType?: string;
  isChained?: boolean;
  gifModel?: string;
}

interface GeneratorContext {
  state: GeneratorState;
  displayLoadingState: DisplayLoadingState;
  displayMedia: DisplayMedia[];
  displayMediaIndex: number;
  activeJob: PollJob | null;
  activeJobRef: React.MutableRefObject<PollJob | null> | null;
  moderationRejectionType: string | null;
  rejectedJobId: string | null;
  tagList: TagGroup[];
  gifTagList: any[];
  jobHistory: HistoryJob[];
  jobHistoryLoading: boolean;
  activeTab: ActiveTab;
  batchSize: number;
  generateStartLoading: boolean;
  generatingQueueType: QueueType;
  tagListLoading: boolean;
  generatorList: Generator[];
  generatorListLoading: boolean;
  generatorListSuccess: boolean;
  gifGeneratorList: GifGenerator[];
  gifGeneratorListLoading: boolean;
  gifGeneratorListSuccess: boolean;
  selectedGifGenerator: GifGenerator | null;
  localeKey: string;
  setTagListLoading: (loading: boolean) => void;
  setGeneratingQueueType: (type: QueueType) => void;
  setGenerateStartLoading: (loading: boolean) => void;
  setActiveTab: (tab: ActiveTab) => void;
  setActiveJob: (job: PollJob | null) => void;
  setActiveJobRef: (job: PollJob | null) => void;
  setDisplayMedia: (media: DisplayMedia[]) => void;
  setDisplayMediaIndex: (index: number) => void;
  setSelectedGenerator: (generator: GeneratorState['selectedGenerator']) => void;
  setSelectedStyleUgc: (style: any) => void;
  setSelectedStyleGroupsUgc: (groups: string[]) => void;
  setSelectedMasterUgc: (master: any) => void;
  setSelectedStyleCreateUgc: (style: any) => void;
  setSelectedStyleGroupCreateUgc: (group: any) => void;
  setPositivePromptCreateUgc: (prompt: string) => void;
  setNegativePromptCreateUgc: (prompt: string) => void;
  setNameCreateUgc: (name: string) => void;
  setCreditCost: (cost: CreditCost) => void;
  setQueueType: (type: QueueType) => void;
  setFreeQueueDelay: (date: Date) => void;
  setTagList: (tags: TagGroup[]) => void;
  setGifTagList: (tags: any[]) => void;
  setCustomPrompt: (prompt: string) => void;
  setCustomNegative: (prompt: string) => void;
  setCfgScale: (scale: number) => void;
  setResolution: (resolution: number) => void;
  setDenoisingStrength: (strength: number) => void;
  setSeed: (seed: number) => void;
  setDisplayLoadingState: (state: DisplayLoadingState) => void;
  setQueuePosition: (position: string) => void;
  setSaveState: (state: number) => void;
  setUpscaleState: (state: number) => void;
  setDetectCelebrity: (detect: boolean) => void;
  setBatchSize: (size: number) => void;
  resetTags: () => void;
  refetchJobHistory: () => void;
  calculateCreditCost: () => number;
  setGenerationType: (type: 'image' | 'gif') => void;
  setCustomPromptGif: (prompt: string) => void;
  setCustomPromptGifImage: (prompt: string) => void;
  setCustomPromptGifMotion: (prompt: string) => void;
  setGifAspectRatio: (ratio: string) => void;
  setGifLength: (length: number) => void;
  setImageToGifLength: (length: number) => void;
  setImageToGifFramerate: (framerate: 30 | 60) => void;
  setSelectedGifGenerator: (generator: GifGenerator | null) => void;
  setSourceImage: (image: SourceImage | null) => void;
  enableImageToGifMode: (preserveDisplayMedia?: boolean) => void;
  enableRegularGifMode: () => void;
  startUpscale: (generationId: string, factor: number) => Promise<void>;
}

const defaultContext: GeneratorContext = {
  state: defaultState,
  displayLoadingState: DisplayLoadingState.Default,
  displayMedia: [],
  displayMediaIndex: 0,
  activeJob: null,
  activeJobRef: null,
  moderationRejectionType: null,
  rejectedJobId: null,
  jobHistory: [],
  jobHistoryLoading: false,
  activeTab: ActiveTab.Generation,
  tagList: [],
  gifTagList: [],
  batchSize: 1,
  generateStartLoading: false,
  generatingQueueType: QueueType.Free,
  tagListLoading: false,
  generatorList: [],
  generatorListLoading: false,
  generatorListSuccess: false,
  gifGeneratorList: [],
  gifGeneratorListLoading: false,
  gifGeneratorListSuccess: false,
  selectedGifGenerator: null,
  localeKey: 'en-US',
  setTagListLoading: () => {},
  setGeneratingQueueType: () => {},
  setGenerateStartLoading: () => {},
  setActiveTab: () => {},
  setActiveJob: () => {},
  setActiveJobRef: () => {},
  setDisplayMedia: () => {},
  setDisplayMediaIndex: () => {},
  setSelectedGenerator: () => {},
  setSelectedStyleUgc: () => {},
  setSelectedStyleGroupsUgc: () => {},
  setSelectedMasterUgc: () => {},
  setSelectedStyleCreateUgc: () => {},
  setSelectedStyleGroupCreateUgc: () => {},
  setPositivePromptCreateUgc: () => {},
  setNegativePromptCreateUgc: () => {},
  setNameCreateUgc: () => {},
  setCreditCost: () => {},
  setQueueType: () => {},
  setFreeQueueDelay: () => {},
  setTagList: () => {},
  setGifTagList: () => {},
  setCustomPrompt: () => {},
  setCustomNegative: () => {},
  setCfgScale: () => {},
  setResolution: () => {},
  setDenoisingStrength: () => {},
  setSeed: () => {},
  setDisplayLoadingState: () => {},
  setQueuePosition: () => {},
  setSaveState: () => {},
  setUpscaleState: () => {},
  setDetectCelebrity: () => {},
  setBatchSize: () => {},
  resetTags: () => {},
  refetchJobHistory: () => {},
  calculateCreditCost: () => 0,
  setGenerationType: () => {},
  setCustomPromptGif: () => {},
  setCustomPromptGifImage: () => {},
  setCustomPromptGifMotion: () => {},
  setGifAspectRatio: () => {},
  setGifLength: () => {},
  setImageToGifLength: () => {},
  setImageToGifFramerate: () => {},
  setSelectedGifGenerator: () => {},
  setSourceImage: () => {},
  enableImageToGifMode: () => {},
  enableRegularGifMode: () => {},
  startUpscale: () => Promise.resolve()
};

const GeneratorContext = createContext<GeneratorContext>(defaultContext);

export const useGenerator = () => useContext(GeneratorContext);

type Props = {
  children: ReactNode;
  localeKey?: string;
};

export function GeneratorContextProvider({ children, localeKey }: Props) {
  const [state, setState] = useState<GeneratorState>(defaultState);
  const [activeJob, setActiveJob] = useState<PollJob | null>(null);
  const activeJobRef = useRef<PollJob | null>(null);
  const { updateCredits } = useGlobalContext();
  const [tagList, setTagList] = useState<TagGroup[]>([]);
  const [gifTagList, setGifTagList] = useState<any[]>([]);
  const [tagListLoading, setTagListLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<ActiveTab>(ActiveTab.Generation);
  const [displayMedia, setDisplayMedia] = useState<DisplayMedia[]>([]);
  const [displayMediaIndex, setDisplayMediaIndex] = useState<number>(0);
  const [displayLoadingState, setDisplayLoadingState] = useState<DisplayLoadingState>(
    DisplayLoadingState.Default
  );
  const [batchSize, setBatchSize] = useState<number>(1);
  const [generateStartLoading, setGenerateStartLoading] = useState<boolean>(false);
  const [generatingQueueType, setGeneratingQueueType] = useState<QueueType>(QueueType.Free);

  const startUpscale = async (generationId: string, factor: number) => {
    try {
      setGenerateStartLoading(true);
      const { response, error } = await upscaleImage({
        generation: generationId,
        upscaleFactor: factor
      });

      if (response) {
        // Don't clear displayMedia - keep showing the current image
        // Update loading state immediately to show we're processing
        setDisplayLoadingState(DisplayLoadingState.Queueing);
        setActiveJobRef({
          jobId: response?.jobId || '',
          delayedUntil: response?.delay || new Date(),
          jobType: JobType.Upscale // Set job type for upscaling
        });
        setGeneratingQueueType(QueueType.Turbo);
      } else if (error) {
        // TODO: show error to user
        console.error('Error upscaling media', error);
        // Maybe set an error state here
      }
    } catch (error) {
      console.error(error);
    } finally {
      setGenerateStartLoading(false);
      setTimeout(() => {
        refetchJobHistory();
      }, 1000);
    }
  };
  const [moderationRejectionType, setModerationRejectionType] = useState<string | null>(null);
  const [rejectedJobId, setRejectedJobId] = useState<string | null>(null);

  const {
    data: jobHistory = [],
    isLoading: jobHistoryLoading,
    refetch: refetchJobHistory
  } = useQuery<HistoryJob[]>({
    queryKey: [JOB_HISTORY_QUERY_KEY],
    queryFn: getJobHistory,
    staleTime: 30000,
    refetchInterval: (query) => {
      // refetchs every 30 seconds if there are active or pending jobs
      const data = query.state.data;
      const hasActiveJobs = data?.some(
        (job: HistoryJob) => job.status === JobStatus.Active || job.status === JobStatus.Pending
      );
      return hasActiveJobs ? 30000 : false;
    }
  });

  const {
    data: generatorList = [],
    isLoading: generatorListLoading,
    refetch: refetchGeneratorList,
    isSuccess: generatorListSuccess
  } = useQuery<GetGeneratorRes[]>({
    queryKey: [GENERATOR_LIST_QUERY_KEY, localeKey],
    queryFn: () => getGeneratorList(localeKey),
    staleTime: 1000,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  });

  // Gif generator list query
  const {
    data: gifGeneratorList = [],
    isLoading: gifGeneratorListLoading,
    refetch: refetchGifGeneratorList,
    isSuccess: gifGeneratorListSuccess
  } = useQuery<GifGenerator[]>({
    queryKey: ['gifGenerators', localeKey],
    queryFn: () => getGifModelList(localeKey!),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    enabled: !!localeKey
  });

  const setActiveJobRef = (job: PollJob | null) => {
    setActiveJob(job);
    activeJobRef.current = job;
  };

  useEffect(() => {
    if (!activeJobRef.current || !activeJob) return;

    const pollInterval = 5000;
    let timeoutId: NodeJS.Timeout;

    const pollGeneration = async () => {
      try {
        if (activeJob.jobId !== activeJobRef.current?.jobId) return;

        const pollRes = await pollGenerationJob({ jobId: activeJob.jobId });

        if (activeJob.jobId !== activeJobRef.current?.jobId) return;

        if (pollRes.response?.status === JobStatus.Delayed) {
          setDisplayLoadingState(DisplayLoadingState.Delaying);
        } else if (pollRes.response?.position) {
          setQueuePosition(pollRes.response.position.toString());
          setDisplayLoadingState(DisplayLoadingState.Queueing);
        } else if (pollRes.response?.status === JobStatus.Active) {
          setDisplayLoadingState(DisplayLoadingState.Generating);
        } else if (pollRes.response?.status === JobStatus.Completed && pollRes.response.result) {
          const media = pollRes.response.result.map((item: MediaResult) => ({
            id: item.id,
            type: item.type,
            imageUrl: item.imageUrl,
            videoUrl: item.videoUrl,
            thumbnailUrl: item.thumbnailUrl,
            saved: false,
            isSaving: false,
            upscaled: item.upscaled || false,
            upscaleFactor: item.upscaleFactor,
            frameRate: item.frameRate,
            generatorId: item.generatorId
          }));
          setDisplayMedia(media);
          setDisplayLoadingState(DisplayLoadingState.Loaded);
          setActiveJobRef(null);
          setModerationRejectionType(null);
          setRejectedJobId(null);
          refetchJobHistory();
        } else if (
          pollRes.response?.status === JobStatus.Cancelled ||
          pollRes.response?.status === JobStatus.Failed
        ) {
          setDisplayMedia([]);
          setActiveJobRef(null);
          setModerationRejectionType(null);
          setRejectedJobId(null);
          setDisplayLoadingState(DisplayLoadingState.ErrorGeneric);
        } else if (pollRes.response?.status === JobStatus.Aborted) {
          setDisplayMedia([]);
          setActiveJobRef(null);
          setModerationRejectionType(pollRes.response?.moderationRejectionType || null);
          setRejectedJobId(pollRes.response?.jobId || null);
          setDisplayLoadingState(DisplayLoadingState.ErrorSensitiveContent);
        }

        // Update credit balance ui on refund
        const creditChanges = pollRes.response?.creditChanges;
        if (creditChanges) {
          updateCredits(creditChanges);
        }

        if (activeJob.jobId === activeJobRef.current?.jobId) {
          timeoutId = setTimeout(() => pollGeneration(), pollInterval);
        }
      } catch (error) {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      }
    };

    pollGeneration();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [activeJob]);

  const setSelectedGenerator = (generator: GeneratorState['selectedGenerator']) => {
    if (!generator) {
      return;
    }
    setState((prev) => ({ ...prev, selectedGenerator: generator }));
  };

  const setSelectedStyleUgc = (style: any) => {
    setState((prev) => ({ ...prev, selectedStyleUgc: style }));
  };

  const setSelectedStyleGroupsUgc = (groups: string[]) => {
    setState((prev) => ({ ...prev, selectedStyleGroupsUgc: groups }));
  };

  const setSelectedMasterUgc = (master: any) => {
    setState((prev) => ({ ...prev, selectedMasterUgc: master }));
  };

  const setSelectedStyleCreateUgc = (style: any) => {
    setState((prev) => ({ ...prev, selectedStyleCreateUgc: style }));
  };

  const setSelectedStyleGroupCreateUgc = (group: any) => {
    setState((prev) => ({ ...prev, selectedStyleGroupCreateUgc: group }));
  };

  const setPositivePromptCreateUgc = (prompt: string) => {
    setState((prev) => ({ ...prev, positivePromptCreateUgc: prompt }));
  };

  const setNegativePromptCreateUgc = (prompt: string) => {
    setState((prev) => ({ ...prev, negativePromptCreateUgc: prompt }));
  };

  const setNameCreateUgc = (name: string) => {
    setState((prev) => ({ ...prev, nameCreateUgc: name }));
  };

  const setCreditCost = (cost: CreditCost) => {
    setState((prev) => ({ ...prev, creditCost: cost }));
  };

  const setQueueType = (type: QueueType) => {
    setState((prev) => ({ ...prev, queueType: type }));
  };

  const setFreeQueueDelay = (date: Date) => {
    setState((prev) => ({ ...prev, freeQueueDelay: date }));
  };

  const setCustomPrompt = (prompt: string) => {
    setState((prev) => ({ ...prev, customPrompt: prompt }));
  };

  const setCustomNegative = (prompt: string) => {
    setState((prev) => ({ ...prev, customNegative: prompt }));
  };

  const setCfgScale = (scale: number) => {
    setState((prev) => ({ ...prev, cfgScale: scale }));
  };

  const setResolution = (resolution: number) => {
    setState((prev) => ({ ...prev, resolution: resolution }));
  };

  const setDenoisingStrength = (strength: number) => {
    setState((prev) => ({ ...prev, denoisingStrength: strength }));
  };

  const setSeed = (seed: number) => {
    setState((prev) => ({ ...prev, seed: seed }));
  };

  const setDisplayImage = (image: string) => {
    setState((prev) => ({ ...prev, displayImage: image }));
  };

  const setQueuePosition = (position: string) => {
    setState((prev) => ({ ...prev, queuePosition: position }));
  };

  const setSaveState = (state: number) => {
    setState((prev) => ({ ...prev, saveState: state }));
  };

  const setUpscaleState = (state: number) => {
    setState((prev) => ({ ...prev, upscaleState: state }));
  };

  const setDetectCelebrity = (detect: boolean) => {
    setState((prev) => ({ ...prev, detectCelebrity: detect }));
  };

  const resetTags = () => {
    setTagList((prev) =>
      prev.map((group) => ({
        ...group,
        tags: group.tags.map((tag) => ({ ...tag, selected: false }))
      }))
    );
  };

  const calculateCreditCost = useCallback(() => {
    const turboCost = state.queueType === QueueType.Turbo ? state.creditCost?.premiumCost : 0;
    const premiumCost = isSDXL(state.selectedGenerator.shortName)
      ? state.creditCost?.generatorCost
      : 0;
    const baseCost = premiumCost + turboCost;
    return baseCost * batchSize;
  }, [state.creditCost, batchSize, state.queueType, state.selectedGenerator._id]);

  const setGenerationType = (type: 'image' | 'gif') => {
    setState((prev) => ({ ...prev, generationType: type }));

    // Auto-select appropriate generator when switching to gif type
    if (type === 'gif' && !selectedGifGenerator) {
      // If source image exists, prefer img2vid generator, otherwise use regular gif generator
      if (state.sourceImage) {
        const img2vidGenerator = gifGeneratorList.find((gen) => gen.workflowType === 'img2vid');
        if (img2vidGenerator) {
          setSelectedGifGenerator(img2vidGenerator);
        }
      } else {
        // Auto-select first non-img2vid generator for regular gif mode
        const regularGifGenerator = gifGeneratorList.find((gen) => gen.workflowType !== 'img2vid');
        if (regularGifGenerator) {
          setSelectedGifGenerator(regularGifGenerator);
        }
      }
    }
  };

  const setCustomPromptGif = (prompt: string) => {
    setState((prev) => ({ ...prev, customPromptGif: prompt }));
  };

  const setCustomPromptGifImage = (prompt: string) => {
    setState((prev) => ({ ...prev, customPromptGifImage: prompt }));
  };

  const setCustomPromptGifMotion = (prompt: string) => {
    setState((prev) => ({ ...prev, customPromptGifMotion: prompt }));
  };

  const setGifAspectRatio = (ratio: string) => {
    setState((prev) => ({ ...prev, gifAspectRatio: ratio }));
  };

  const setGifLength = (length: number) => {
    setState((prev) => {
      // Reset to 30fps if selecting non-5s length
      const newFramerate =
        length !== 5 && prev.imageToGifFramerate === 60 ? 30 : prev.imageToGifFramerate;
      return { ...prev, gifLength: length, imageToGifFramerate: newFramerate };
    });
  };

  const setImageToGifLength = (length: number) => {
    setState((prev) => ({ ...prev, imageToGifLength: length }));
  };

  const setImageToGifFramerate = (framerate: 30 | 60) => {
    setState((prev) => ({ ...prev, imageToGifFramerate: framerate }));
  };

  const setSourceImage = (image: SourceImage | null) => {
    setState((prev) => {
      // When setting a source GIF with 60fps, auto-set framerate to 60
      let newFramerate = prev.imageToGifFramerate;
      if (image?.isGif && image.frameRate === 60) {
        newFramerate = 60;
      } else if (image?.isGif && image.frameRate === 30) {
        // Reset to 30fps for 30fps GIFs
        newFramerate = 30;
      }
      return { ...prev, sourceImage: image, imageToGifFramerate: newFramerate };
    });
  };

  const enableImageToGifMode = (preserveDisplayMedia: boolean = false) => {
    // Switch to gif mode and select img2vid generator
    setState((prev) => ({ ...prev, generationType: 'gif' }));

    // Only clear display media when navigating from external sources (post page)
    // Preserve it when navigating from History tab
    if (!preserveDisplayMedia) {
      setDisplayMedia([]);
      setDisplayLoadingState(DisplayLoadingState.Default);
    }

    // Find and select the img2vid generator
    const img2vidGenerator = gifGeneratorList.find((gen) => gen.workflowType === 'img2vid');
    if (img2vidGenerator) {
      setSelectedGifGenerator(img2vidGenerator);
    } else if (gifGeneratorList.length > 0) {
      // If no img2vid generator found, wait for the list to load and try again
      setTimeout(() => {
        const retryImg2vidGenerator = gifGeneratorList.find(
          (gen) => gen.workflowType === 'img2vid'
        );
        if (retryImg2vidGenerator) {
          setSelectedGifGenerator(retryImg2vidGenerator);
        }
      }, 100);
    }
  };

  const enableRegularGifMode = () => {
    // Switch to gif mode and select regular (non-img2vid) generator
    setState((prev) => ({ ...prev, generationType: 'gif' }));
    const regularGifGenerator = gifGeneratorList.find((gen) => gen.workflowType !== 'img2vid');
    if (regularGifGenerator) {
      setSelectedGifGenerator(regularGifGenerator);
    }
  };

  // Add gif generator state
  const [selectedGifGenerator, setSelectedGifGenerator] = useState<GifGenerator | null>(null);

  // Auto-select first gif generator and load from localStorage (only when not on image tab and no source image)
  useEffect(() => {
    if (
      gifGeneratorList.length > 0 &&
      !selectedGifGenerator &&
      state.generationType !== 'image' &&
      !state.sourceImage
    ) {
      // Check localStorage for cached gif generator
      const cachedGifGeneratorId = localStorage.getItem('selectedGifGenerator');
      const cachedGifGenerator = cachedGifGeneratorId
        ? gifGeneratorList.find((generator) => generator._id === cachedGifGeneratorId)
        : null;

      // Use cached generator if available, otherwise use first non-img2vid one
      const generatorToUse =
        cachedGifGenerator ||
        gifGeneratorList.find((gen) => gen.workflowType !== 'img2vid') ||
        gifGeneratorList[0];
      setSelectedGifGenerator(generatorToUse);
    }
  }, [gifGeneratorList, selectedGifGenerator, state.sourceImage]);

  const contextValues = {
    state,
    displayLoadingState,
    displayMedia,
    displayMediaIndex,
    activeJob,
    activeJobRef,
    moderationRejectionType,
    rejectedJobId,
    tagList,
    gifTagList,
    jobHistory,
    jobHistoryLoading,
    activeTab,
    batchSize,
    generateStartLoading,
    generatingQueueType,
    tagListLoading,
    generatorList,
    generatorListLoading,
    generatorListSuccess,
    gifGeneratorList,
    gifGeneratorListLoading,
    gifGeneratorListSuccess,
    selectedGifGenerator,
    localeKey: localeKey || 'en-US',
    setTagListLoading,
    setGeneratingQueueType,
    setGenerateStartLoading,
    setActiveTab,
    setActiveJob,
    setActiveJobRef,
    setDisplayMedia,
    setDisplayMediaIndex,
    setSelectedGenerator,
    setSelectedStyleUgc,
    setSelectedStyleGroupsUgc,
    setSelectedMasterUgc,
    setSelectedStyleCreateUgc,
    setSelectedStyleGroupCreateUgc,
    setPositivePromptCreateUgc,
    setNegativePromptCreateUgc,
    setNameCreateUgc,
    setCreditCost,
    setQueueType,
    setFreeQueueDelay,
    setTagList,
    setGifTagList,
    setCustomPrompt,
    setCustomNegative,
    setCfgScale,
    setResolution,
    setDenoisingStrength,
    setSeed,
    setDisplayLoadingState,
    setQueuePosition,
    setSaveState,
    setUpscaleState,
    setDetectCelebrity,
    setBatchSize,
    resetTags,
    refetchJobHistory,
    calculateCreditCost,
    setGenerationType,
    setCustomPromptGif,
    setCustomPromptGifImage,
    setCustomPromptGifMotion,
    setGifAspectRatio,
    setGifLength,
    setImageToGifLength,
    setImageToGifFramerate,
    setSelectedGifGenerator,
    setSourceImage,
    enableImageToGifMode,
    enableRegularGifMode,
    startUpscale
  };

  return <GeneratorContext.Provider value={contextValues}>{children}</GeneratorContext.Provider>;
}
